import React, {
  FC,
  ReactNode,
  PropsWithChildren,
  useState,
  createContext,
  memo,
  useRef,
  useEffect,
  useCallback
} from 'react';

import { classNames } from 'shared/utils/class-names';
import { useMidScreen } from 'shared/utils/hooks/useMidScreen';
import { CloseIcon } from '../Icons';

import classes from './styles.module.scss';

export const SidebarContext = createContext<{ onClose: (() => void) | null }>({
  onClose: () => {
    console.log('close');
  }
});

type InnerContentProps = {
  isInlineMode: boolean;
  setShowContent: (value: boolean) => void;
};

const InnerContent: FC<PropsWithChildren<InnerContentProps>> = memo(
  ({ isInlineMode, setShowContent, children }) => (
    <SidebarContext.Provider
      value={{
        onClose: !isInlineMode ? () => setShowContent(false) : null
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
);

interface ISidebar {
  icon: ReactNode;
  notClosable?: boolean;
  onClose?: () => void;
  sidebarWidth?: number;
  isOpenFromOutside?: boolean;
}

export const Sidebar: FC<PropsWithChildren<ISidebar>> = ({
  icon,
  notClosable = false,
  children,
  onClose,
  sidebarWidth = 320,
  isOpenFromOutside = false
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const isMidScreen = useMidScreen();
  const isInlineMode = notClosable && isMidScreen;
  const [showContent, setShowContent] = useState<boolean>(isInlineMode);

  useEffect(() => {
    if (contentRef.current && sidebarWidth) {
      const left =
        isInlineMode || showContent || isOpenFromOutside
          ? `-${sidebarWidth}px`
          : '0';
      contentRef.current.style.width = `${sidebarWidth}px`;
      contentRef.current.style.left = left;
    }
  }, [sidebarWidth, isInlineMode, showContent, isOpenFromOutside]);

  const closeByClickOutside = useCallback(() => {
    if (isOpenFromOutside && onClose) {
      onClose();
    } else {
      setShowContent(false);
    }
  }, [isOpenFromOutside, onClose]);

  return (
    <>
      {(showContent || isOpenFromOutside) && !notClosable && (
        <div className={classes.overlay} onClick={closeByClickOutside} />
      )}
      <div className={classes.wrapper}>
        <div
          className={classNames(classes.content, {
            [classes['content-open']]:
              isInlineMode || showContent || isOpenFromOutside,
            [classes['content-inline']]: isInlineMode && !isOpenFromOutside
          })}
          ref={contentRef}
        >
          <div className={classes.content__top}>
            <div className={classes.logoIcon}>{icon}</div>
            {onClose && (
              <div className={classes.closeIcon} onClick={onClose}>
                <CloseIcon />
              </div>
            )}
          </div>
          <InnerContent
            isInlineMode={isInlineMode}
            setShowContent={setShowContent}
          >
            {children}
          </InnerContent>
        </div>
        <div className={classes.sidebar}>
          <button
            className={classes.sidebar__btn}
            onClick={() => setShowContent((prevState) => !prevState)}
            disabled={isInlineMode}
          >
            <div className={classes.logoIcon}>{icon}</div>
          </button>
        </div>
      </div>
    </>
  );
};
